import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/restet";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const restet: CaseStudy = {
  slug: "restet",
  title: "Restet",
  cardTitle: "Разработка и продвижение сайта биотехнологического бренда Restet",
  subtitle: "Биотехнологический косметический бренд",
  industry: { label: "Красота и косметология", slug: "beauty" },
  eyebrow: "Корпоративный сайт · Биотехнологии и косметика",
  h1: "Разработка и продвижение сайта Restet",
  lead:
    "Собрали сайт биотехнологического косметического бренда Restet: продуктовую часть, корпоративный biotech-контекст, разделы о компонентах, исследованиях и технологиях — и ведём продвижение проекта.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт и продвижение бренда" },

  seo: {
    primaryKeyword: "разработка и продвижение сайта биотехнологического косметического бренда",
    secondaryKeywords: [
      "сайт косметического бренда разработка",
      "продвижение бренда косметики",
      "сайт biotech компании",
      "сайт производителя косметических ингредиентов",
    ],
    semanticEntities: [
      "продукты и бренды",
      "компоненты",
      "научные исследования",
      "технологии",
      "лаборатория",
      "B2B-направление",
      "маркетплейсы",
    ],
    excludedKeywords: [
      "крем от псориаза купить",
      "anti-age крем",
      "бактериородопсин купить",
      "косметика от экземы",
    ],
    title: "Сайт и продвижение Restet — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт Restet для биотехнологического косметического бренда — продукты, технологии, исследования, B2B-направление и продвижение.",
    h1: "Разработка и продвижение сайта Restet",
    slug: "restet",
    canonicalUrl: `${SITE_URL}/cases/restet`,
    ogTitle: "Сайт биотехнологического бренда Restet",
    ogDescription:
      "Продуктовая часть и корпоративный biotech-контекст: компоненты, исследования, технологии, B2B-направление и продвижение.",
    ogImage: `${SITE_URL}${dir}/restet-og.webp`,
    robotsIndex: true,
    datePublished: "2024-02-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка корпоративных сайтов",
      to: routes.corporate,
      note: "Продуктовые и научные разделы бренда",
    },
    relatedIndustry: {
      label: "Продвижение брендов красоты и косметологии",
      to: routes.seo,
      note: "SEO и контент для косметического бизнеса",
    },
    relatedCases: [
      {
        label: "Сайт и продвижение сети клиник Quantum Clinic",
        to: "/cases/quantum-clinic",
        note: "Экспертный бренд в сфере красоты и медицины",
      },
      {
        label: "Интернет-магазин профессиональной косметики Quantum Shop",
        to: "/cases/quantum-shop",
        note: "Каталог косметического бренда и карточка товара",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен сайт для biotech или косметического бренда?",
    text: "Соберём продуктовую часть и научный раздел бренда, подготовим структуру к продвижению.",
    button: "Обсудить сайт и продвижение бренда",
  },

  client: {
    name: "Restet",
    url: "https://restet.ru/",
    city: "Москва",
    industry: "Биотехнологии и косметика",
  },
  year: "С 2024 года",
  services: [
    "Дизайн корпоративного сайта",
    "Разработка продуктовых разделов",
    "Разделы технологий и исследований",
    "B2B-раздел",
    "SEO-продвижение",
    "Сопровождение проекта",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Корпоративный сайт", to: routes.corporate },
    { label: "Разработка", to: routes.sites },
    { label: "SEO", to: routes.seo },
    { label: "Поддержка", to: routes.corporate },
  ],
  facts: [
    { label: "Клиент", value: "Restet" },
    { label: "Отрасль", value: "Биотехнологии и косметика" },
    { label: "Тип проекта", value: "Корпоративный сайт с продуктовым и научным разделами" },
    { label: "Платформа", value: "1С-Битрикс" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Сайт, объединяющий продуктовую часть и корпоративный biotech-контекст",
      "Разделы для брендов, компонентов, научных исследований и технологий",
      "Раздел о собственной лаборатории и научных партнёрах",
      "Информация об участии специалистов Сеченовского университета и статусе резидента Сколково",
      "Переходы на покупку через маркетплейсы и Л'Этуаль",
      "Структура разделов, подготовленная к продвижению",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Restet — биотехнологический бренд, где продукт неотделим от науки: покупателю и партнёру важно видеть косметическую линейку вместе с компонентной базой, исследованиями и лабораторией, которая стоит за брендом.",
        "Сайту нужно было выдержать два уровня продукта одновременно — потребительский уход и научный, B2B-контекст, — не превращая при этом сайт в псевдомедицинский ресурс.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: чистый biotech-стиль",
    paragraphs: [
      "Палитра построена на молочном и графите с одним фирменным акцентом — это держит бренд в поле косметики, а не медицины.",
      "Продукт и научная графика показаны предметно, без псевдомедицинских иллюстраций и стоковых образов лаборатории.",
    ],
    bullets: [
      "Молочно-графитовая палитра с фирменным акцентом",
      "Продукт и научная графика без псевдомедицинского визуала",
      "Единая структура для продуктовых и научных разделов",
    ],
  },

  identity: {
    fonts: [
      { name: "Manrope", role: "Заголовки", sample: "Технологии Restet" },
      { name: "Circe", role: "Текст", sample: "Компоненты и исследования" },
    ],
    colors: [
      { hex: "#F4F1EC", name: "Молочный фон" },
      { hex: "#2A2A2A", name: "Графит" },
      { hex: "#7C8F73", name: "Фирменный акцент" },
    ],
  },

  cover: img(
    "restet-case-cover",
    "Главная страница сайта биотехнологического бренда Restet",
    6,
  ),

  gallery: [
    img("restet-products", "Продуктовые разделы сайта Restet", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия сохраняет оба уровня продукта: переход к потребительской линейке и к научному разделу доступен с первого экрана.",
      "Карточки продуктов и переходы на маркетплейсы выстроены горизонтальным скроллом, чтобы не перегружать экран.",
    ],
    images: [
      mobile("restet-mobile-home", "Мобильная главная сайта Restet"),
      mobile("restet-mobile-products", "Мобильные продуктовые разделы Restet"),
    ],
  },

  columnBlocks: [
    {
      id: "levels",
      title: "Два уровня продукта",
      paragraphs: [
        "Потребительский уход и научное B2B-направление собраны в одном понятном блоке, чтобы посетитель сразу видел, к какому сценарию он относится.",
      ],
      columns: [
        { title: "Потребитель", items: ["Линейка ухода", "Описание продукта"] },
        { title: "B2B и наука", items: ["Компоненты", "Исследования", "Технологии"] },
      ],
    },
    {
      id: "tech",
      title: "Технология и исследования",
      paragraphs: [
        "Отдельные разделы объясняют компонент, механику «как работает», исследования и партнёров бренда — включая участие специалистов Сеченовского университета и статус резидента Сколково.",
      ],
      bullets: [
        "Раздел о компоненте и механике действия",
        "Научные исследования и партнёры",
        "Собственная лаборатория бренда",
      ],
      images: [
        img("restet-tech", "Раздел технологий и исследований на сайте Restet", 6),
      ],
    },
    {
      id: "purchase",
      title: "Продукт и каналы покупки",
      paragraphs: [
        "Каталог продукции связан с переходами на крупные маркетплейсы и Л'Этуаль — отдельный сценарий оформления заказа на сайте не имитируется.",
      ],
      bullets: ["Каталог продукции бренда", "Переходы на маркетплейсы", "Переход на Л'Этуаль"],
    },
    {
      id: "promotion",
      title: "Продвижение проекта",
      paragraphs: [
        "Сайт остаётся на нашем сопровождении: работаем со структурой разделов и продвижением бренда. Показатели раскроем после сверки аналитики и согласования с клиентом.",
      ],
      bullets: [
        "SEO продуктовых и научных разделов",
        "Контентная работа со структурой сайта",
        "Техническая поддержка проекта",
      ],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка корпоративного сайта", to: routes.corporate },
      { label: "SEO-продвижение", to: routes.seo },
      { label: "Поддержка и развитие", to: routes.corporate },
    ],
  },
};
